import * as React from 'react'
import { CircleXIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react'

import { cn } from '@/components/new/lib/utils'
import { Badge } from '@/components/new/ui/badge'
import type { Color } from '@/components/new/ui/colors'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/new/ui/popover'

type InfoSeverity = 'info' | 'warning' | 'error'

const severityIconClassName: Record<InfoSeverity, string> = {
    info: 'text-blue-600',
    warning: 'text-amber-600',
    error: 'text-red-600',
}

type InfoBadgeProps = Omit<React.ComponentPropsWithoutRef<'span'>, 'children' | 'color'> & {
    label?: React.ReactNode
    children?: React.ReactNode
    badge?: React.ReactNode
    severity?: InfoSeverity
    small?: boolean
    variant?: 'outline'
    color?: Color | `#${string}`
    popoverClassName?: string
    contentClassName?: string
    align?: React.ComponentProps<typeof PopoverContent>['align']
    alignOffset?: React.ComponentProps<typeof PopoverContent>['alignOffset']
    side?: React.ComponentProps<typeof PopoverContent>['side']
    sideOffset?: React.ComponentProps<typeof PopoverContent>['sideOffset']
}

const InfoBadge = ({
    label,
    children,
    className,
    popoverClassName,
    contentClassName,
    badge,
    severity = 'info',
    small = false,
    variant = 'outline',
    color,
    onClick,
    align = 'end',
    alignOffset,
    side,
    sideOffset,
    ...props
}: InfoBadgeProps) => {
    const hasContent = React.Children.count(children) > 0
    const Icon = severity === 'error' ? CircleXIcon : severity === 'warning' ? TriangleAlertIcon : InfoIcon

    const iconClassName = cn(
        'rounded-full',
        severityIconClassName[severity],
        hasContent && 'cursor-pointer hover:opacity-80 active:opacity-60',
        small ? 'size-[var(--text-xs)]' : 'size-[var(--text-base)]'
    )

    const handleClick = React.useCallback(
        (event: React.MouseEvent<HTMLSpanElement>) => {
            event.stopPropagation()
            onClick?.(event)
        },
        [onClick]
    )

    const trigger = badge ? (
        <span
            className={cn(
                'inline-flex w-fit shrink-0 items-center align-middle leading-none',
                hasContent && 'cursor-pointer',
                className
            )}
            onClick={handleClick}
            {...props}
        >
            {badge}
        </span>
    ) : label ? (
        <Badge
            variant={variant}
            color={color}
            className={cn(hasContent && 'cursor-pointer', small && 'h-4 px-1 text-[10px]', className)}
            onClick={handleClick}
            {...props}
        >
            {label ?? <Icon className={severityIconClassName[severity]} />}
        </Badge>
    ) : (
        <span
            className={cn('inline-flex w-fit shrink-0 leading-none', hasContent && 'cursor-pointer', className)}
            onClick={handleClick}
            {...props}
        >
            <Icon className={iconClassName} />
        </span>
    )

    if (!hasContent) {
        return trigger
    }

    return (
        <Popover>
            <PopoverTrigger nativeButton={false} render={trigger} />
            <PopoverContent
                align={align}
                alignOffset={alignOffset}
                side={side}
                sideOffset={sideOffset}
                className={cn('w-auto p-3', popoverClassName)}
            >
                <div
                    className={cn('max-w-xs', typeof children === 'string' && 'whitespace-pre-wrap', contentClassName)}
                    onClick={(event) => event.stopPropagation()}
                >
                    {children}
                </div>
            </PopoverContent>
        </Popover>
    )
}

export { InfoBadge, type InfoBadgeProps, type InfoSeverity }
